import React, { useState, useEffect } from 'react';
import { Tabs, Spin, notification, Modal } from 'antd';
import { useParams } from 'react-router-dom';
import { getDetail } from '../../store/summary/action';
import { formatTimestampToDate } from '../../utils/convertUtil';
import styles from './Detail.module.scss';

import NetworkInfo from './component/network/NetworkInfo';
import NetworkPci from './component/network/NetworkPci';
import NetworkRouting from './component/network/NetworkRouting';
import NetworkNtp from './component/network/NetworkNtp';

import MountInfo from './component/mount/MountInfo';

import DiskInfo from './component/disk/DiskInfo';
import PartitionInfo from './component/disk/PartitionInfo';
import LvmInfo from './component/disk/LvmInfo';
import FilesystemInfo from './component/disk/FilesystemInfo';

import LogViewer from './component/log/LogViewer';

import PackageList from './component/system/PackageList';
import BootParams from './component/system/BootParams';
import TunedInfo from './component/system/TunedInfo';
import KernelParams from './component/system/KernelParams';
import ModuleList from './component/system/ModuleList';
import ProcessList from './component/system/ProcessList';

import ServiceList from './component/service/ServiceList';
import SecurityInfo from './component/security/SecurityInfo';

const { TabPane } = Tabs;

const Detail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [sosData, setSosData] = useState({});
  const [activeTab, setActiveTab] = useState('1');
  const [uploadDate, setUploadDate] = useState('');
  const [extractedDate, setExtractedDate] = useState('');
  const [fileName, setFileName] = useState('');

  const fetchSosReportData = async () => {
    try {
      setLoading(true);
      const response = await getDetail(id);
      
      if (response) {
        setSosData(response);
        
        // 기본 정보 설정
        if (response.insDate) {
          setUploadDate(formatTimestampToDate(response.insDate));
        }
        if (response.extractedDate) {
          setExtractedDate(formatTimestampToDate(response.extractedDate));
        }
        if (response.filename) {
          setFileName(response.filename);
        }
      }
    } catch (error) {
      notification.error({
        message: '데이터 로드 실패',
        description: '상세 정보를 불러오는 중 오류가 발생했습니다.',
        placement: 'top'
      });
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSosReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div className={styles.wrapperDetail}>
      <div className={`${styles.pageTitle} p-16`}>
        <h1>Detail</h1>
        {fileName && <div className={styles.fileInfo}>파일명: {fileName}</div>}
        <div className={styles.dateInfo}>
          {uploadDate && <span>업로드: {uploadDate}</span>}
          {extractedDate && <span>추출: {extractedDate}</span>}
        </div>
      </div>

      {loading ? (
        <Modal
          open={loading}
          footer={null}
          closable={false}
          centered
          width={300}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '10px' }}>데이터 로딩 중...</p>
          </div>
        </Modal>
      ) : (
        <div className={styles.contentContainer}>
          <Tabs activeKey={activeTab} onChange={handleTabChange} type="card">
            <TabPane tab="네트워크 정보" key="1">
              <div className={styles.tabContent}>
                <NetworkInfo networkData={sosData.networkIP} />
                <NetworkPci pciData={sosData.pciInfo} />
                <NetworkRouting routeInfo={sosData.routeInfo} />
                <NetworkNtp ntpConfig={sosData.ntpConfig} ntpStatus={sosData.ntpStatus} />
              </div>
            </TabPane>
            
            <TabPane tab="마운트 정보" key="2">
              <div className={styles.tabContent}>
                <MountInfo fstabInfo={sosData.fstabInfo} mountInfo={sosData.mountInfo} />
              </div>
            </TabPane>
            
            <TabPane tab="디스크 정보" key="3">
              <div className={styles.tabContent}>
                <DiskInfo diskInfo={sosData.diskInfo} />
                <PartitionInfo partitionInfo={sosData.partitionInfo} />
                <LvmInfo lvmInfo={sosData.lvmInfo} />
                <FilesystemInfo filesystemInfo={sosData.filesystemInfo} />
              </div>
            </TabPane>
            
            <TabPane tab="로그 분석" key="4">
              <div className={styles.tabContent}>
                <LogViewer 
                  messages={sosData.messages}
                  dmesg={sosData.dmesg}
                  syslog={sosData.syslog}
                  bootlog={sosData.bootlog}
                />
              </div>
            </TabPane>
            
            <TabPane tab="패키지 및 파라미터" key="5">
              <div className={styles.tabContent}>
                <PackageList packageList={sosData.packageList} />
                <BootParams bootParams={sosData.bootParams} />
                <TunedInfo tunedInfo={sosData.tunedInfo} />
                <KernelParams kernelParams={sosData.kernelParams} />
              </div>
            </TabPane>
            
            <TabPane tab="서비스 및 프로세스" key="6">
              <div className={styles.tabContent}>
                <ServiceList serviceList={sosData.serviceList} />
                <ModuleList moduleList={sosData.moduleList} />
                <ProcessList processList={sosData.processList} />
              </div>
            </TabPane>
            
            <TabPane tab="보안 설정" key="7">
              <div className={styles.tabContent}>
                <SecurityInfo securityInfo={sosData.securityInfo} />
              </div>
            </TabPane>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default Detail;